import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@repo/shared";
import { Form, Container, Row, Col, Card, Button, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUpload, faTrash } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory, { Type } from 'react-bootstrap-table2-editor';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import LoadingSpinner from '../common/LoadingSpinner';

import PlanDataService from "../../services/class/PlanService";

export default function Plans({ user }) {
  const { appTheme } = useTheme();
  const [themes, setThemes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(true);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [chosenFile, setChosenFile] = useState();
  const [uploadCompleteMessage, setUploadCompleteMessage] = useState('');

  const fileInputRef = useRef();

  const updateAndSortMaterials = (newMaterials) => {
    setMaterials(newMaterials.sort((a, b) => {
      const prioritySort = (a.priority || 99) - (b.priority || 99);
      const levelSort = (a.level || 0) - (b.level || 0);
      const nameSort = a.name.localeCompare(b.name);
      return prioritySort || nameSort || levelSort;
    }));
  };

  const updateAndSortThemes = (newThemes) => {
    setThemes(newThemes.sort((a, b) => (a.priority || 99) - (b.priority || 99)));
  };

  useEffect(() => {
    const retrieveThemes = () => {
      setLoadingThemes(true);
      PlanDataService.getThemes()
        .then(updateAndSortThemes)
        .catch(console.error)
        .finally(() => setLoadingThemes(false));
    };

    const retrieveMaterials = () => {
      setLoadingMaterials(true);
      PlanDataService.getMaterials()
        .then(updateAndSortMaterials)
        .catch(console.error)
        .finally(() => setLoadingMaterials(false));
    };

    document.title = `${appTheme?.appName || ''} - Plans`;
    retrieveThemes();
    retrieveMaterials();
  }, []);

  const handleDeleteTheme = async (rowId) => {
    setLoadingThemes(true);
    try {
      const incomingThemes = await PlanDataService.deleteTheme(rowId);
      updateAndSortThemes(incomingThemes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingThemes(false);
    }
  };

  const handleDeleteMaterial = async (rowId) => {
    setLoadingMaterials(true);
    try {
      const incomingMaterials = await PlanDataService.deleteMaterial(rowId);
      updateAndSortMaterials(incomingMaterials);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const addTheme = () => {
    const newTheme = {
      id: `t-${themes.length + 1}`,
      name: `New Theme ${themes.length + 1}`,
      priority: `${themes.length + 1}`
    };
    setThemes([newTheme, ...themes]);
  };

  const themeCellSaved = async (oldValue, newValue, row, column) => {
    row[column.dataField] = newValue;
    setLoadingThemes(true);
    try {
      const incomingThemes = await PlanDataService.saveTheme(row);
      updateAndSortThemes(incomingThemes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingThemes(false);
    }
  };
  
  const materialCellSaved = async (oldValue, newValue, row, column) => {
    setLoadingMaterials(true);
    try {
      const incomingMaterials = await PlanDataService.saveMaterial(row);
      updateAndSortMaterials(incomingMaterials);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMaterials(false);
    }
  };
  
  const uploadChosenFile = async () => {
    if (!chosenFile) return;
    setUploadCompleteMessage('Uploading...');
    setLoadingMaterials(true);
    try {
      const incomingMaterials = await PlanDataService.uploadMaterial(chosenFile);
      updateAndSortMaterials(incomingMaterials);
      setChosenFile(null);
      setUploadCompleteMessage('Upload successful!');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => setUploadCompleteMessage(''), 3000); // Clear message after 3 seconds
    } catch (e) {
      console.error(e);
      setUploadCompleteMessage(`Upload failed.`);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const themeColumns = [
    { dataField: 'name', text: 'Name', filter: textFilter() },
    { dataField: 'priority', text: 'Priority', filter: textFilter() },
    {
      dataField: 'ideas',
      text: 'Ideas',
      // Style the cell to wrap long text when not in edit mode
      style: {
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word'
      },
      editor: {
        type: Type.TEXTAREA,
        // Apply custom styles to the textarea editor when activated
        style: {
          height: '200px',
          resize: 'vertical' // Allow the user to resize the textarea
        }
      }
    },
    ...(user?.role.includes('admin') ? [{
      dataField: 'actions',
      text: 'Actions',
      editable: false,
      formatter: (cell, row) => (
        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTheme(row.id)}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      ),
      headerStyle: { width: '80px', textAlign: 'center' },
      align: 'center',
    }] : [])
  ];

  const materialColumns = [
    {
      dataField: 'name',
      text: 'Document',
      editable: false,
      formatter: (cell, row) => <a href={row.url} target="_blank" rel="noopener noreferrer">{row.name}</a>,
    },
    { dataField: 'level', text: 'Level' },
    {
      dataField: 'theme',
      text: 'Theme',
      editor: {
        type: Type.SELECT,
        options: themes.map(p => ({ value: p.name, label: p.name })),
      }
    },
    ...(user?.role.includes('admin') ? [{
      dataField: 'actions',
      text: 'Actions',
      editable: false,
      formatter: (cell, row) => (
        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMaterial(row.id)}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      ),
      headerStyle: { width: '80px', textAlign: 'center' },
      align: 'center',
    }] : [])
  ];

  const tableNoDataIndication = (loading, count, name) => {
    if (loading) return <div className="p-4"><LoadingSpinner /></div>;
    if (count === 0) return <div className="text-center p-4 text-muted">No {name} Found</div>;
    return null;
  };

  return (
    <Container fluid className="py-4">
      <h1 className="mb-4">Lesson Plans</h1>
      <Row xs={1} lg={2} className="g-4">
        {/* THEMES CARD */}
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Themes</h5>
              {user?.role.includes('admin') && (
                <Button variant="primary" size="sm" onClick={addTheme}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Theme
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              <BootstrapTable
                keyField='id'
                data={themes}
                columns={themeColumns}
                bordered={false}
                classes="mb-0"
                noDataIndication={() => tableNoDataIndication(loadingThemes, themes.length, 'Themes')}
                cellEdit={cellEditFactory({
                  mode: 'click',
                  blurToSave: true,
                  afterSaveCell: themeCellSaved,
                })}
                filter={filterFactory()}
              />
            </Card.Body>
            <Card.Footer className="text-muted">
              Showing {themes.length} themes.
            </Card.Footer>
          </Card>
        </Col>

        {/* MATERIALS CARD */}
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Materials</h5>
            </Card.Header>
            {user?.role === 'admin' && (
              <Card.Body>
                <h6>Upload New Material</h6>
                <InputGroup>
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => setChosenFile(e.target.files[0])}
                  />
                  <Button variant="outline-secondary" onClick={uploadChosenFile} disabled={!chosenFile || loadingMaterials}>
                    <FontAwesomeIcon icon={faUpload} className="me-2" /> Upload
                  </Button>
                </InputGroup>
                {uploadCompleteMessage && <Form.Text className="text-muted mt-2">{uploadCompleteMessage}</Form.Text>}
              </Card.Body>
            )}
            <Card.Body className="p-0 border-top">
               <BootstrapTable
                keyField='id'
                data={materials}
                columns={materialColumns}
                bordered={false}
                classes="mb-0"
                noDataIndication={() => tableNoDataIndication(loadingMaterials, materials.length, 'Materials')}
                cellEdit={cellEditFactory({
                  mode: 'click',
                  blurToSave: true,
                  afterSaveCell: materialCellSaved,
                })}
              />
            </Card.Body>
            <Card.Footer className="text-muted">
              Showing {materials.length} materials.
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}